import { api } from "../config/api";
import { post, postAllowHttpError } from "../api";
import { usersService } from "./users.service";
import type { UsersSessionArgs } from "./users.service";

export interface ChatSessionArgs extends UsersSessionArgs {
  sender_username?: string | null;
}

export interface GetClientsChatResponse {
  success?: boolean;
  data?: { clients?: ChatClientApiItem[] };
  errors?: string;
}

export interface GetConversationUsersResponse {
  success?: boolean;
  data?: ActiveConvoApiItem[] | null;
  meta?: Record<string, unknown>;
  errors?: string | string[];
  error_code?: string | null;
}

export interface ChatClientApiItem {
  email: string;
  client_id: string;
  client_name: string;
  client_pan?: string;
  country_code?: string;
  phone?: string;
  is_logged?: boolean;
  is_active?: boolean;
  profile_pic_url?: string | null;
  user_info: { user_id: string };
}

export interface ActiveConvoApiItem {
  i?: number;
  business_id?: number;
  conversation_id: number;
  receiver_username: string;
  first_name?: string;
  surname?: string;
  uuid?: string;
  message_id: number;
  lastest_message: string;
  time_stamp: string;
  sender_role?: string;
  receiver_role: string;
  sender_client_name?: string | null;
  receiver_client_name?: string | null;
  unread_count?: number;
}

function pickNonNegativeInt(v: unknown): number | undefined {
  if (v === null || v === undefined || v === "") return undefined;
  const n = typeof v === "string" ? parseInt(v, 10) : Number(v);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.floor(n);
}

function extractUnreadFromConversationRaw(r: Record<string, unknown>): number | undefined {
  return (
    pickNonNegativeInt(r.unread_count) ??
    pickNonNegativeInt(r.UnreadCount) ??
    pickNonNegativeInt(r.unreadCount) ??
    pickNonNegativeInt(r.unread_messages) ??
    pickNonNegativeInt(r.unread_message_count) ??
    pickNonNegativeInt(r.unread_messages_count) ??
    pickNonNegativeInt(r.total_unread) ??
    pickNonNegativeInt(r.unread) ??
    pickNonNegativeInt(r.new_messages) ??
    pickNonNegativeInt(r.new_message_count)
  );
}

export function normalizeActiveConversationItem(raw: ActiveConvoApiItem): ActiveConvoApiItem {
  const r = raw as ActiveConvoApiItem & Record<string, unknown>;
  const unread = extractUnreadFromConversationRaw(r);
  if (unread === undefined) return raw;
  return { ...raw, unread_count: unread };
}

export function getConversationUnreadCount(item: unknown): number {
  if (typeof item !== "object" || item === null) return 0;
  return extractUnreadFromConversationRaw(item as Record<string, unknown>) ?? 0;
}

export interface GetConversationIdArgs extends UsersSessionArgs {
  receiver_username: string;
  sender_username: string;
}

export interface GetConversationIdResponse {
  success?: boolean;
  data?: {
    business_id?: number;
    conversation_id: number;
    sender_type?: string;
  };
  errors?: string;
}

export interface GetMessagesArgs extends UsersSessionArgs {
  user: string;
  conversation_id: number;
  page_number?: number;
  page_size?: number;
}

export interface ChatMessageApiItem {
  conversation_id: number;
  message_id: number;
  user: string;
  message: string;
  timestamp: string;
}

export interface GetMessagesResponse {
  success?: boolean;
  data?: {
    messages?: ChatMessageApiItem[];
    pagination?: {
      current_page?: number;
      total_pages?: number;
      total_messages?: number;
      has_next?: boolean;
      has_previous?: boolean;
    };
  };
  meta?: Record<string, unknown>;
  errors?: string | string[];
  error_code?: string | null;
}

export interface CreateMessageArgs extends UsersSessionArgs {
  user: string;
  conversation_id: number;
  message: string;
}

export interface CreateMessageResponse {
  success?: boolean;
  data?: unknown;
  errors?: string;
}

function getConversationId(args: GetConversationIdArgs): Promise<GetConversationIdResponse> {
  const { business_id, device_token, session_id, receiver_username, sender_username } = args;
  if (!business_id || !device_token || !session_id || !receiver_username || !sender_username) {
    return Promise.reject(new Error("Session, receiver_username and sender_username required"));
  }
  return post<GetConversationIdResponse>(api.GET_CONVERSATION_ID, {
    business_id,
    device_token,
    session_id,
    receiver_username,
    sender_username,
  });
}

function getMessages(args: GetMessagesArgs): Promise<GetMessagesResponse> {
  const {
    business_id,
    device_token,
    session_id,
    user,
    conversation_id,
    page_number = 1,
    page_size = 50,
  } = args;
  if (!business_id || !device_token || !session_id || !user) {
    return Promise.reject(new Error("Session and user required"));
  }
  const businessIdNum = business_id !== null ? Number(business_id) : undefined;
  return postAllowHttpError<GetMessagesResponse>(api.GET_MESSAGES, {
    business_id: businessIdNum,
    device_token,
    session_id,
    user,
    conversation_id,
    page_number,
    page_size,
  });
}

function createMessage(args: CreateMessageArgs): Promise<CreateMessageResponse> {
  const { business_id, device_token, session_id, user, conversation_id, message } = args;
  if (!business_id || !device_token || !session_id || !user || message === null) {
    return Promise.reject(new Error("Session, user and message required"));
  }
  const businessIdNum = Number(business_id);
  return post<CreateMessageResponse>(api.CREATE_MESSAGES, {
    business_id: businessIdNum,
    device_token,
    session_id,
    user,
    conversation_id,
    message,
  });
}

function getClientsForChat(
  args: ChatSessionArgs & { client_type?: string }
): Promise<GetClientsChatResponse> {
  const { business_id, device_token, session_id, client_type = "REGISTERED" } = args;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject(new Error("Session required"));
  }
  return post<GetClientsChatResponse>(api.GET_CLIENTS, {
    business_id,
    device_token,
    session_id,
    client_type,
  });
}

function getConversationUsers(args: ChatSessionArgs): Promise<GetConversationUsersResponse> {
  const { business_id, device_token, session_id, sender_username } = args;
  if (!business_id || !device_token || !session_id || !sender_username) {
    return Promise.reject(new Error("Session and sender_username required"));
  }
  return post<GetConversationUsersResponse>(api.GET_CONVERSATION_USERS, {
    business_id,
    device_token,
    session_id,
    sender_username,
  });
}

export const chatService = {
  getBusinessTeam: usersService.getBusinessTeam,
  getClientsForChat,
  getConversationUsers,
  getConversationId,
  getMessages,
  createMessage,
};
